import React, {createContext, ReactNode} from 'react';
import {
	BeforeCapture,
	Direction,
	DragDropContext,
	Draggable,
	DraggableId,
	DraggableProvided,
	DraggableRubric,
	DraggableStateSnapshot,
	DragStart,
	DragUpdate,
	DroppableId,
	DroppableMode,
	DroppableProvided,
	DroppableStateSnapshot,
	DropResult,
	OnBeforeCaptureResponder,
	OnBeforeDragStartResponder,
	OnDragEndResponder,
	OnDragStartResponder,
	OnDragUpdateResponder,
	ResponderProvided,
	TypeId,
} from 'react-beautiful-dnd';
import {InnerDragNDropContext} from './context';
import {DragDropManager, DragDropManagerRef} from './drag-drop-manager';


class ContextWrapperProps {
	onBeforeCapture?: OnBeforeCaptureResponder;
	onBeforeDragStart?: OnBeforeDragStartResponder;
	onDragStart?: OnDragStartResponder;
	onDragUpdate?: OnDragUpdateResponder;
	onDragEnd?: OnDragEndResponder;
	dragDropManagerRef?: DragDropManagerRef;
	children: ReactNode;
}

export class DragDropRoot extends React.PureComponent<ContextWrapperProps> {
	private manager: DragDropManager = new DragDropManager();

	public override componentDidMount() {
		if (this.props.dragDropManagerRef) {
			this.props.dragDropManagerRef.current = this.manager;
		}
	}

	private onBeforeCapture = (before: BeforeCapture) => {
		this.props.onBeforeCapture && this.props.onBeforeCapture(before);
		this.manager.triggerBeforeCapture(before);
	}
	private onBeforeDragStart = (start: DragStart) => {
		this.props.onBeforeDragStart && this.props.onBeforeDragStart(start);
		this.manager.triggerBeforeDragStart(start);
	}

	private onDragStart = (start: DragStart, provided: ResponderProvided) => {
		this.props.onDragStart && this.props.onDragStart(start, provided);
		this.manager.triggerDragStart(start, provided);
	}

	private onDragUpdate = (update: DragUpdate, provided: ResponderProvided) => {
		this.props.onDragUpdate && this.props.onDragUpdate(update, provided);
		this.manager.triggerDragUpdate(update, provided);
	}
	private onDragEnd = (result: DropResult, provided: ResponderProvided) => {
		this.props.onDragEnd && this.props.onDragEnd(result, provided);
		this.manager.triggerDragEnd(result, provided);
	}

	public override render() {
		return <InnerDragNDropContext.Provider value={{manager: this.manager}}><DragDropContext
			onBeforeCapture={this.onBeforeCapture}
			onBeforeDragStart={this.onBeforeDragStart}
			onDragStart={this.onDragStart}
			onDragUpdate={this.onDragUpdate}
			onDragEnd={this.onDragEnd}>
			{this.props.children}
		</DragDropContext>
		</InnerDragNDropContext.Provider>;
	}
}

export type DroppableChildrenFn = (provided: DroppableProvided, snapshot: DroppableStateSnapshot) => React.ReactElement<HTMLElement>;

class DraggableWrapperProps {
	children?: (
		provided: DraggableProvided,
		snapshot: DraggableStateSnapshot,
		rubric: DraggableRubric,
	) => React.ReactElement<HTMLElement> | React.ReactNode

	disableInteractiveElementBlocking: boolean;
	draggableId: DraggableId;
	index: number;
	isDragDisabled: boolean;
	shouldRespectForcePress: boolean;
	className?: string;
}

export class DraggableWrapper extends React.PureComponent<DraggableWrapperProps> {
	public override render() {
		return <Draggable draggableId={this.props.draggableId}
		                  isDragDisabled={this.props.isDragDisabled}
		                  disableInteractiveElementBlocking={this.props.disableInteractiveElementBlocking}
		                  shouldRespectForcePress={this.props.shouldRespectForcePress}
		                  index={this.props.index}>
			{(provided, snapshot, rubric) => this.renderInner(provided, snapshot, rubric)}
		</Draggable>
	}

	private renderInner(provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: DraggableRubric) {
		const children = this.props.children as any;
		if (typeof children === 'function') {
			return children(provided, snapshot, rubric);
		} else {
			return <div ref={provided.innerRef}
			            {...provided.draggableProps}
			            {...provided.dragHandleProps} className={this.props.className || ''}>
				{children}
			</div>
		}
	}
}
